/**
 * Catálogo único de permissões da área da empresa. O backend valida cada rota contra este catálogo;
 * a interface apenas o usa para esconder ações. Papéis são configuráveis por empresa.
 */

export const PERMISSION_GROUPS = [
  {
    key: 'overview',
    label: 'Visão geral',
    permissions: [{ key: 'dashboard.view', label: 'Ver visão geral financeira' }],
  },
  {
    key: 'payables',
    label: 'Contas a pagar',
    permissions: [
      { key: 'payables.view', label: 'Ver contas a pagar' },
      { key: 'payables.create', label: 'Criar e editar lançamentos' },
      { key: 'payables.request_approval', label: 'Solicitar aprovação' },
      { key: 'payables.settle', label: 'Registrar baixa (pagamento confirmado no banco)' },
      { key: 'payables.adjust', label: 'Registrar ajustes, estornos e cancelamentos' },
    ],
  },
  {
    key: 'approvals',
    label: 'Aprovações',
    permissions: [
      { key: 'approvals.view', label: 'Ver fila de aprovações' },
      { key: 'approvals.decide', label: 'Aprovar ou recusar obrigações' },
    ],
  },
  {
    key: 'receivables',
    label: 'Contas a receber',
    permissions: [
      { key: 'receivables.view', label: 'Ver contas a receber' },
      { key: 'receivables.create', label: 'Criar e editar recebíveis' },
      { key: 'receivables.settle', label: 'Registrar recebimentos' },
      { key: 'receivables.adjust', label: 'Registrar ajustes, estornos e cancelamentos' },
    ],
  },
  {
    key: 'bank',
    label: 'Contas bancárias',
    permissions: [
      { key: 'bank.view', label: 'Ver contas, saldos e movimentos' },
      { key: 'bank.manage', label: 'Cadastrar contas e saldo inicial' },
      { key: 'bank.transfer', label: 'Registrar transferências internas' },
    ],
  },
  {
    key: 'registry',
    label: 'Cadastros',
    permissions: [
      { key: 'registry.view', label: 'Ver cadastros' },
      { key: 'registry.manage', label: 'Gerenciar cadastros' },
    ],
  },
  {
    key: 'reports',
    label: 'Relatórios',
    permissions: [
      { key: 'reports.view', label: 'Ver relatórios' },
      { key: 'reports.export', label: 'Exportar relatórios' },
    ],
  },
  {
    key: 'documents',
    label: 'Documentos',
    permissions: [
      { key: 'attachments.view', label: 'Ver e baixar anexos' },
      { key: 'attachments.upload', label: 'Enviar anexos e comprovantes' },
    ],
  },
  {
    key: 'settings',
    label: 'Configurações',
    permissions: [
      { key: 'settings.company', label: 'Editar perfil da empresa' },
      { key: 'settings.users', label: 'Gerenciar usuários e papéis' },
      { key: 'settings.subscription', label: 'Ver assinatura e uso dos limites' },
      { key: 'imports.manage', label: 'Importar planilhas' },
      { key: 'audit.view', label: 'Ver auditoria da empresa' },
    ],
  },
] as const;

export type Permission = (typeof PERMISSION_GROUPS)[number]['permissions'][number]['key'];

export const ALL_PERMISSIONS: Permission[] = PERMISSION_GROUPS.flatMap((g) =>
  g.permissions.map((p) => p.key as Permission),
);

export function isPermission(value: string): value is Permission {
  return (ALL_PERMISSIONS as string[]).includes(value);
}

const READ: Permission[] = [
  'dashboard.view',
  'payables.view',
  'approvals.view',
  'receivables.view',
  'bank.view',
  'registry.view',
  'reports.view',
  'attachments.view',
];

export type SystemRoleKey = 'admin' | 'operator' | 'approver' | 'viewer';

/** Papéis padrão criados em toda empresa nova. Podem ser editados pela própria empresa. */
export const DEFAULT_ROLES: Record<SystemRoleKey, { name: string; description: string; permissions: Permission[] }> = {
  admin: {
    name: 'Administrador da empresa',
    description: 'Cadastros, configurações, usuários e acompanhamento financeiro.',
    permissions: [
      ...READ,
      'payables.create',
      'payables.request_approval',
      'payables.adjust',
      'receivables.create',
      'receivables.adjust',
      'bank.manage',
      'bank.transfer',
      'registry.manage',
      'reports.export',
      'attachments.upload',
      'settings.company',
      'settings.users',
      'settings.subscription',
      'imports.manage',
      'audit.view',
    ],
  },
  operator: {
    name: 'Operador financeiro',
    description: 'Lançamentos, solicitações, documentos e registro de baixas.',
    permissions: [
      ...READ,
      'payables.create',
      'payables.request_approval',
      'payables.settle',
      'receivables.create',
      'receivables.settle',
      'bank.transfer',
      'registry.manage',
      'reports.export',
      'attachments.upload',
    ],
  },
  approver: {
    name: 'Aprovador',
    description: 'Análise e autorização das obrigações.',
    permissions: [...READ, 'approvals.decide', 'reports.export'],
  },
  viewer: {
    name: 'Consulta',
    description: 'Acesso de leitura aos recursos autorizados.',
    permissions: [...READ],
  },
};

/** Papéis globais da plataforma — nunca atribuídos por cadastro comum. */
export const PLATFORM_ROLES = {
  superadmin: { name: 'Superadministrador', description: 'Acesso total à administração do SaaS.' },
  operator: { name: 'Operador da plataforma', description: 'Gerencia empresas e assinaturas; não gerencia administradores.' },
  viewer: { name: 'Leitura da plataforma', description: 'Consulta empresas, planos e assinaturas.' },
} as const;

export type PlatformRole = keyof typeof PLATFORM_ROLES;

export const PLATFORM_CAPABILITIES = {
  'platform.view': ['superadmin', 'operator', 'viewer'],
  'platform.tenants.manage': ['superadmin', 'operator'],
  'platform.subscriptions.manage': ['superadmin', 'operator'],
  'platform.plans.manage': ['superadmin'],
  'platform.users.manage': ['superadmin'],
  'platform.audit.view': ['superadmin', 'operator', 'viewer'],
} as const satisfies Record<string, readonly PlatformRole[]>;

export type PlatformCapability = keyof typeof PLATFORM_CAPABILITIES;

export function platformCan(role: PlatformRole | null | undefined, cap: PlatformCapability): boolean {
  if (!role) return false;
  return (PLATFORM_CAPABILITIES[cap] as readonly string[]).includes(role);
}
